/**
 * Configuration-driven Kanban organism.
 * Pure board engine — domain logic stays in consumers (e.g. CandidateKanban).
 *
 * DnD session state lives in useKanbanDnd. Column CRUD chrome lives in
 * KanbanManageDialogs. Pure insertion/collision helpers live in kanban.utils.
 */

import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  defaultDropAnimationSideEffects,
  useSensor,
  useSensors,
  type DropAnimation,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Checkbox } from '../../atoms/Checkbox';
import { Typography } from '../../atoms/Typography';
import { cn } from '@/utils/cn';
import { KanbanBoard } from './KanbanBoard';
import { KanbanColumn } from './KanbanColumn';
import { KanbanColumnCards } from './KanbanColumnCards';
import { KanbanOverlay } from './KanbanOverlay';
import { KanbanDragOverlay } from './KanbanDragOverlay';
import { KanbanColumnMenu } from './KanbanColumnMenu';
import { KanbanToolbar } from './KanbanToolbar';
import { KanbanManageDialogs } from './KanbanManageDialogs';
import { KanbanColumnInsertZone } from './KanbanColumnInsertZone';
import { KanbanTrailingAddColumn } from './KanbanTrailingAddColumn';
import { useKanbanDnd } from './useKanbanDnd';
import { useKanbanFlip } from './useKanbanFlip';
import { useKanbanDropLanding } from './useKanbanDropLanding';
import { useKanbanColumnInsertIntent } from './useKanbanColumnInsertIntent';
import { scrollElementIntoHorizontalView } from '@/utils/scroll-horizontal-into-view';
import {
  getKanbanDensityShellClass,
  kanbanColumnCountClass,
  kanbanColumnDragPreviewClass,
  kanbanShellClass,
  kanbanSwimlaneLabelClass,
  KANBAN_COLUMN_OVERLAY_WIDTH,
  toColumnSortId,
} from './Kanban.styles';
import type { KanbanItemBase, KanbanProps } from './Kanban.config.types';
import {
  createKanbanCollisionDetection,
  filterDefsForColumn,
  filterKanbanItems,
  getColumnInsertAriaLabel,
  groupKanbanItems,
  normalizeColumnOrder,
  resolveKanbanFeatures,
  resolvePendingColumnInsertIndex,
  resolveKanbanColumnTitleLabel,
  resolveReassignColumnId,
  sortKanbanItems,
} from './kanban.utils';

/** Stable default so memoized cards are not invalidated every render. */
const NO_SELECTED_IDS: string[] = [];

/** Column drags only; card drops use useKanbanDropLanding. */
const columnDropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.4' } },
  }),
};

export function Kanban<TItem extends KanbanItemBase>({
  columns,
  items,
  renderCard,
  renderOverlay,
  features: featuresProp,
  className,
  headerSlot,
  toolbarExtra,
  showBoardToolbar = true,
  isLoading,
  emptyBoardMessage,
  columnOrder: columnOrderProp,
  onColumnOrderChange,
  onCardMove,
  onColumnAdd,
  onColumnRename,
  onColumnDelete,
  onColumnCollapsedChange,
  onWipViolation,
  searchQuery = '',
  onSearchQueryChange,
  getSearchText,
  filterDefs = [],
  activeFilters = {},
  onActiveFiltersChange,
  sortOptions = [],
  sortBy = null,
  onSortByChange,
  columnSortBy = {},
  onColumnSortByChange,
  columnFilters = {},
  onColumnFiltersChange,
  groupOptions = [],
  groupBy = null,
  onGroupByChange,
  density: densityProp = 'comfortable',
  onDensityChange,
  views = [],
  activeViewId = null,
  onActiveViewChange,
  selectedIds = NO_SELECTED_IDS,
  onSelectedIdsChange,
  isItemSelectable,
  bulkActions = [],
  onBulkAction,
  sensors: sensorsProp,
  collisionDetection: collisionDetectionProp,
  onDropPreviewChange,
  scrollToColumnId = null,
  onScrollToColumnHandled,
  showTrailingAddColumn = false,
  trailingAddColumnLabel = 'Add column',
  addColumnDialogLabels,
}: KanbanProps<TItem>) {
  const features = resolveKanbanFeatures(featuresProp);
  const density = densityProp;
  const shellRef = useRef<HTMLDivElement>(null);
  // Flip before landing: both are layout effects and landing cancels the
  // dropped card's flip.
  const captureFlip = useKanbanFlip(shellRef);
  const landCard = useKanbanDropLanding(shellRef);
  const collisionDetection =
    collisionDetectionProp ?? createKanbanCollisionDetection();

  const defaultSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const sensors = sensorsProp ?? defaultSensors;

  const columnOrder = useMemo(
    () => normalizeColumnOrder(columns, columnOrderProp),
    [columns, columnOrderProp]
  );

  const columnsById = useMemo(() => {
    const map = new Map(columns.map((column) => [column.id, column]));
    return map;
  }, [columns]);

  const [renameTarget, setRenameTarget] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [addColumnOpen, setAddColumnOpen] = useState(false);
  const [addColumnTitle, setAddColumnTitle] = useState('');
  const [isAddSubmitting, setIsAddSubmitting] = useState(false);
  /** Remembered while Add Column dialog is open; `null` = append (toolbar). */
  const [pendingInsertAtIndex, setPendingInsertAtIndex] = useState<
    number | null
  >(null);

  const visibleItems = useMemo(() => {
    const filtered = filterKanbanItems(items, {
      searchQuery: features.search ? searchQuery : '',
      getSearchText,
      filterDefs: features.filters ? filterDefs : [],
      activeFilters: features.filters ? activeFilters : {},
    });
    return sortKanbanItems(
      filtered,
      features.sorting ? sortOptions : undefined,
      features.sorting ? sortBy : null
    );
  }, [
    items,
    features.search,
    features.filters,
    features.sorting,
    searchQuery,
    getSearchText,
    filterDefs,
    activeFilters,
    sortOptions,
    sortBy,
  ]);

  const itemsById = useMemo(() => {
    const map = new Map<string, TItem>();
    for (const item of visibleItems) map.set(item.id, item);
    return map;
  }, [visibleItems]);

  const groups = useMemo(
    () =>
      groupKanbanItems(
        visibleItems,
        features.swimlanes ? groupOptions : undefined,
        features.swimlanes ? groupBy : null
      ),
    [visibleItems, features.swimlanes, groupOptions, groupBy]
  );

  const swimlanesEnabled = Boolean(features.swimlanes && groupBy);
  /** Column DnD conflicts with multi-lane droppable ids — disable while swimlanes are on. */
  const enableColumnDrag = features.columnDrag && !swimlanesEnabled;
  const columnSortIds = useMemo(
    () => (enableColumnDrag ? columnOrder.map(toColumnSortId) : undefined),
    [enableColumnDrag, columnOrder]
  );

  const {
    activeDragType,
    lastDragType,
    overColumnId,
    dropPreview,
    settling,
    activeItem,
    activeColumnId,
    activeColumn,
    getLaneColumnItems,
    makeDroppableId,
    handleDragStart,
    handleDragOver,
    handleDragMove,
    handleDragEnd,
    handleDragCancel,
  } = useKanbanDnd({
    features: {
      cardDrag: features.cardDrag,
      cardReorder: features.cardReorder,
      wipLimits: features.wipLimits,
      wipBehavior: features.wipBehavior,
      swimlanes: features.swimlanes,
      filters: features.filters,
      sorting: features.sorting,
    },
    items,
    itemsById,
    visibleItems,
    groups,
    columns,
    columnsById,
    columnOrder,
    enableColumnDrag,
    swimlanesEnabled,
    groupBy,
    groupOptions,
    columnFilters,
    columnSortBy,
    filterDefs,
    sortOptions,
    onCardMove,
    onColumnOrderChange,
    onWipViolation,
    onDropPreviewChange,
    onLayoutWillChange: captureFlip,
    onCardLanding: landCard,
  });
  const settlingItem = settling ? itemsById.get(settling.itemId) ?? null : null;

  const columnInsertEnabled =
    features.columnManage &&
    Boolean(onColumnAdd) &&
    !swimlanesEnabled &&
    activeDragType == null;
  const {
    armedInsertSlot,
    onPointerIntentStart,
    onPointerIntentEnd,
    disarmAll: disarmColumnInsert,
  } = useKanbanColumnInsertIntent(columnInsertEnabled);

  // Scroll a newly created (or requested) column into view once it mounts.
  useLayoutEffect(() => {
    if (!scrollToColumnId) return;
    const root = shellRef.current;
    if (!root) return;

    const column = root.querySelector<HTMLElement>(
      `[data-kanban-column-id="${CSS.escape(scrollToColumnId)}"]`,
    );
    if (!column) return;

    const isFirst = columnOrder[0] === scrollToColumnId;
    const isLast = columnOrder[columnOrder.length - 1] === scrollToColumnId;
    scrollElementIntoHorizontalView(
      column,
      isFirst ? 'start' : isLast ? 'end' : 'nearest',
    );

    onScrollToColumnHandled?.();
  }, [scrollToColumnId, columnOrder, onScrollToColumnHandled]);

  const overlayCtx = {
    density,
    isSelected: false,
    selectionEnabled: false,
    onToggleSelect: () => undefined,
    selectionControl: null,
    isOverlay: true,
    columnItemIds: [],
  };

  const deleteReassignId = deleteTargetId
    ? resolveReassignColumnId(columnOrder, deleteTargetId)
    : null;
  const deleteReassignTitle = deleteReassignId
    ? columnsById.get(deleteReassignId)?.title
    : null;

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    Object.values(activeFilters).some((value) => value != null && value !== '');

  const showToolbar =
    showBoardToolbar &&
    (features.search ||
      features.filters ||
      features.sorting ||
      features.swimlanes ||
      features.density ||
      features.views ||
      features.bulkActions ||
      features.columnManage ||
      Boolean(toolbarExtra));

  const renderColumnsForItems = (laneItems: TItem[], groupId: string) => {
    const columnTitles = columnOrder.map((columnId) => {
      const column = columnsById.get(columnId);
      return column ? resolveKanbanColumnTitleLabel(column) : columnId;
    });
    const showInsertZones =
      features.columnManage && Boolean(onColumnAdd) && !swimlanesEnabled;
    const nodes: ReactNode[] = [];

    const pushInsertZone = (insertAtIndex: number) => {
      if (!showInsertZones) return;
      nodes.push(
        <KanbanColumnInsertZone
          key={`column-insert:${groupId}:${insertAtIndex}`}
          insertAtIndex={insertAtIndex}
          ariaLabel={getColumnInsertAriaLabel({
            insertAtIndex,
            columnTitles,
          })}
          isArmed={columnInsertEnabled && armedInsertSlot === insertAtIndex}
          disabled={!columnInsertEnabled}
          onPointerIntentStart={() => onPointerIntentStart(insertAtIndex)}
          onPointerIntentEnd={() => onPointerIntentEnd(insertAtIndex)}
          onAdd={() => {
            disarmColumnInsert();
            setPendingInsertAtIndex(insertAtIndex);
            setAddColumnTitle('');
            setAddColumnOpen(true);
          }}
        />
      );
    };

    pushInsertZone(0);

    columnOrder.forEach((columnId, columnIndex) => {
      const column = columnsById.get(columnId);
      if (!column) return;
      const columnFilterState = columnFilters[columnId] ?? {};
      const columnSort = columnSortBy[columnId] ?? null;
      const columnItems = getLaneColumnItems(laneItems, columnId);
      const count = columnItems.length;
      const wipLimit = features.wipLimits ? column.wipLimit : null;
      const collapsed = Boolean(column.collapsed);
      const droppableId = makeDroppableId(groupId, column.id);
      const selectableColumnItems = isItemSelectable
        ? columnItems.filter((item) => isItemSelectable(item))
        : columnItems;
      const selectedInColumn = selectableColumnItems.filter((item) =>
        selectedIds.includes(item.id)
      ).length;
      const allSelected =
        selectableColumnItems.length > 0 &&
        selectedInColumn === selectableColumnItems.length;
      const someSelected =
        selectedInColumn > 0 &&
        selectedInColumn < selectableColumnItems.length;
      const showColumnSelect =
        features.selection && !collapsed && selectableColumnItems.length > 0;

      nodes.push(
        <KanbanColumn
          key={droppableId}
          id={droppableId}
          columnId={column.id}
          droppableData={{ columnId: column.id, groupId }}
          count={count}
          wipLimit={wipLimit}
          itemIds={collapsed ? [] : columnItems.map((item) => item.id)}
          isDropTarget={
            activeDragType === 'card' && overColumnId === droppableId
          }
          isCollapsed={collapsed}
          canCollapse={features.columnCollapse}
          onCollapse={
            features.columnCollapse
              ? () => onColumnCollapsedChange?.(column.id, true)
              : undefined
          }
          onExpand={
            collapsed
              ? () => onColumnCollapsedChange?.(column.id, false)
              : undefined
          }
          enableColumnDrag={enableColumnDrag}
          header={column.title}
          headerStart={
            showColumnSelect ? (
              <div
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <Checkbox
                  size="sm"
                  shape="circle"
                  checked={allSelected}
                  indeterminate={someSelected}
                  disabled={selectableColumnItems.length === 0}
                  onChange={() => {
                    const ids = selectableColumnItems.map((item) => item.id);
                    if (allSelected) {
                      onSelectedIdsChange?.(
                        selectedIds.filter((id) => !ids.includes(id))
                      );
                    } else {
                      onSelectedIdsChange?.(
                        Array.from(new Set([...selectedIds, ...ids]))
                      );
                    }
                  }}
                  aria-label="Select all cards in column"
                />
              </div>
            ) : null
          }
          headerActions={
            collapsed ? null : (
              <KanbanColumnMenu
                canRename={
                  features.columnManage && column.canRename !== false
                }
                canDelete={
                  features.columnManage &&
                  columns.length > 1 &&
                  column.canDelete !== false
                }
                onRename={() => {
                  const current = resolveKanbanColumnTitleLabel(column);
                  setRenameTarget({ id: column.id, title: current });
                  setRenameValue(current);
                }}
                onDelete={() => setDeleteTargetId(column.id)}
                sortOptions={
                  features.sorting
                    ? sortOptions.map((option) => ({
                        id: option.id,
                        label: option.label,
                      }))
                    : []
                }
                sortBy={columnSort}
                onSortByChange={
                  features.sorting
                    ? (next) =>
                        onColumnSortByChange?.({
                          ...columnSortBy,
                          [column.id]: next,
                        })
                    : undefined
                }
                filterDefs={
                  features.filters
                    ? filterDefsForColumn(filterDefs, column.id).map((def) => ({
                        id: def.id,
                        label: def.label,
                        options: def.options,
                      }))
                    : []
                }
                activeFilters={columnFilterState}
                onActiveFiltersChange={
                  features.filters
                    ? (next) =>
                        onColumnFiltersChange?.({
                          ...columnFilters,
                          [column.id]: next,
                        })
                    : undefined
                }
              />
            )
          }
        >
          {collapsed ? null : (
            <KanbanColumnCards
              droppableId={droppableId}
              columnItems={columnItems}
              activeItem={activeItem}
              dropPreview={dropPreview}
              settling={settling}
              settlingItem={settlingItem}
              renderCard={renderCard}
              density={density}
              cardDragEnabled={features.cardDrag}
              selectionEnabled={features.selection}
              isItemSelectable={isItemSelectable}
              selectedIds={selectedIds}
              onSelectedIdsChange={onSelectedIdsChange}
            />
          )}
        </KanbanColumn>
      );

      pushInsertZone(columnIndex + 1);
    });

    if (
      showTrailingAddColumn &&
      features.columnManage &&
      Boolean(onColumnAdd) &&
      !swimlanesEnabled
    ) {
      nodes.push(
        <KanbanTrailingAddColumn
          key={`trailing-add:${groupId}`}
          label={trailingAddColumnLabel}
          disabled={!columnInsertEnabled}
          onAdd={() => {
            disarmColumnInsert();
            setPendingInsertAtIndex(columnOrder.length);
            setAddColumnTitle('');
            setAddColumnOpen(true);
          }}
        />,
      );
    }

    return nodes;
  };

  return (
    <div
      ref={shellRef}
      className={cn(
        kanbanShellClass,
        getKanbanDensityShellClass(density),
        className
      )}
    >
      {headerSlot}

      {showToolbar ? (
        <KanbanToolbar
          showSearch={features.search}
          searchQuery={searchQuery}
          onSearchQueryChange={onSearchQueryChange}
          showFilters={features.filters}
          filterDefs={filterDefs}
          activeFilters={activeFilters}
          onActiveFiltersChange={onActiveFiltersChange}
          showSorting={features.sorting}
          sortOptions={sortOptions}
          sortBy={sortBy}
          onSortByChange={onSortByChange}
          showSwimlanes={features.swimlanes}
          groupOptions={groupOptions}
          groupBy={groupBy}
          onGroupByChange={onGroupByChange}
          showDensity={features.density}
          density={density}
          onDensityChange={onDensityChange}
          showViews={features.views}
          views={views}
          activeViewId={activeViewId}
          onActiveViewChange={(viewId) => {
            onActiveViewChange?.(viewId);
            const view = views.find((entry) => entry.id === viewId);
            if (!view) return;
            if (view.searchQuery != null) onSearchQueryChange?.(view.searchQuery);
            if (view.filters) onActiveFiltersChange?.(view.filters);
            if (view.sortBy !== undefined) onSortByChange?.(view.sortBy ?? null);
            if (view.groupBy !== undefined) onGroupByChange?.(view.groupBy ?? null);
            if (view.density) onDensityChange?.(view.density);
          }}
          showBulkActions={features.bulkActions && features.selection}
          selectedCount={selectedIds.length}
          bulkActions={bulkActions}
          onBulkAction={(actionId) => onBulkAction?.(actionId, selectedIds)}
          showColumnManage={features.columnManage}
          onAddColumn={() => {
            disarmColumnInsert();
            setPendingInsertAtIndex(null);
            setAddColumnTitle('');
            setAddColumnOpen(true);
          }}
          visibleCount={
            hasActiveFilters || features.search ? visibleItems.length : undefined
          }
          hasActiveFilters={hasActiveFilters}
          onClearFilters={() => {
            onSearchQueryChange?.('');
            onActiveFiltersChange?.(
              Object.fromEntries(filterDefs.map((def) => [def.id, null]))
            );
          }}
          extra={toolbarExtra}
        />
      ) : null}

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <Typography variant="body-sm" color="secondary">
            Loading board…
          </Typography>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden relative">
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            {groups.map((group) => (
              <div
                key={group.groupId}
                className="flex flex-col min-h-0 flex-1 border-b border-dark-invirtus-soft last:border-b-0"
              >
                {group.label != null ? (
                  <div className={kanbanSwimlaneLabelClass}>
                    <Typography variant="body-sm" weight="semibold">
                      {group.label}
                    </Typography>
                  </div>
                ) : null}
                {/*
                  Parent owns DndContext so swimlanes share one drag session.
                  KanbanBoard only provides the scroll row + optional column SortableContext.
                */}
                <KanbanBoard
                  sensors={sensors}
                  withoutDndContext
                  columnIds={columnSortIds}
                  className="flex-1"
                  rowClassName={
                    features.columnManage && onColumnAdd ? 'gap-0' : undefined
                  }
                >
                  {renderColumnsForItems(group.items, group.groupId)}
                </KanbanBoard>
              </div>
            ))}
            <KanbanDragOverlay
              itemsById={itemsById}
              renderCard={renderOverlay ?? renderCard}
              density={density}
              dropAnimation={
                lastDragType === 'column' ? columnDropAnimation : null
              }
              columnOverlay={
                activeColumn && activeColumnId ? (
                <KanbanOverlay width={KANBAN_COLUMN_OVERLAY_WIDTH}>
                  <div className={kanbanColumnDragPreviewClass}>
                    <div className="flex items-center gap-2 px-3 py-2.5 border-b border-dark-invirtus-soft/60 shrink-0">
                      <Typography
                        variant="body-sm"
                        weight="semibold"
                        className="truncate flex-1"
                      >
                        {activeColumn.title}
                      </Typography>
                      <span className={kanbanColumnCountClass}>
                        {
                          visibleItems.filter(
                            (item) => item.columnId === activeColumnId
                          ).length
                        }
                      </span>
                    </div>
                    <div className="flex-1 min-h-0 p-2 space-y-2 overflow-hidden">
                      {visibleItems
                        .filter((item) => item.columnId === activeColumnId)
                        .slice(0, 5)
                        .map((item) => (
                          <div key={item.id} className="pointer-events-none">
                            {(renderOverlay ?? renderCard)(item, overlayCtx)}
                          </div>
                        ))}
                    </div>
                  </div>
                </KanbanOverlay>
                ) : null
              }
            />
          </DndContext>

          {visibleItems.length === 0 && emptyBoardMessage ? (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <Typography variant="body-sm" color="muted">
                {emptyBoardMessage}
              </Typography>
            </div>
          ) : null}
        </div>
      )}

      <KanbanManageDialogs
        addOpen={addColumnOpen}
        onAddOpenChange={(open) => {
          if (isAddSubmitting) return;
          setAddColumnOpen(open);
          if (!open) {
            setPendingInsertAtIndex(null);
            setAddColumnTitle('');
          }
        }}
        addTitle={addColumnTitle}
        onAddTitleChange={setAddColumnTitle}
        isAddSubmitting={isAddSubmitting}
        addDialogLabels={addColumnDialogLabels}
        onAddConfirm={async (title) => {
          const insertAtIndex = resolvePendingColumnInsertIndex(
            pendingInsertAtIndex,
            columnOrder.length
          );
          setIsAddSubmitting(true);
          try {
            await onColumnAdd?.(title, { insertAtIndex });
            setPendingInsertAtIndex(null);
          } finally {
            setIsAddSubmitting(false);
          }
        }}
        renameTarget={renameTarget}
        onRenameTargetClear={() => setRenameTarget(null)}
        renameValue={renameValue}
        onRenameValueChange={setRenameValue}
        onRenameConfirm={(columnId, title) =>
          onColumnRename?.(columnId, title)
        }
        deleteTargetId={deleteTargetId}
        onDeleteTargetClear={() => setDeleteTargetId(null)}
        deleteReassignId={deleteReassignId}
        deleteReassignTitle={deleteReassignTitle}
        onDeleteConfirm={(columnId, meta) => onColumnDelete?.(columnId, meta)}
      />
    </div>
  );
}
